import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  COLOR_ESTADO_FACTURA,
  ETIQUETA_ESTADO_FACTURA,
  formatearMonedaArs,
  type FacturaDTO,
  type TarifaEmpresaDTO,
} from '@ausentia/shared';
import { abrirPdf, api, ErrorApi } from '../api/cliente.js';
import { useToast } from '../componentes/Toast.js';

/** Primer y último día del mes actual (AAAA-MM-DD), por defecto del período. */
function mesActual(): { desde: string; hasta: string } {
  const hoy = new Date();
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    desde: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    hasta: iso(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)),
  };
}

export function Facturas() {
  const toast = useToast();
  const inicial = mesActual();
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [empresaId, setEmpresaId] = useState('');
  const [empresas, setEmpresas] = useState<{ id: string; nombre: string }[]>([]);
  const [facturas, setFacturas] = useState<FacturaDTO[] | null>(null);
  const [emitiendo, setEmitiendo] = useState(false);
  const [error, setError] = useState('');

  const refrescar = useCallback(async () => {
    setFacturas(await api.get<FacturaDTO[]>('/api/operador/facturas'));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const tarifas = await api.get<TarifaEmpresaDTO[]>('/api/operador/tarifas');
        const unicas = new Map<string, string>();
        for (const t of tarifas) unicas.set(t.empresaId, t.empresaNombre);
        const lista = [...unicas].map(([id, nombre]) => ({ id, nombre }));
        setEmpresas(lista);
        if (lista[0]) setEmpresaId(lista[0].id);
        await refrescar();
      } catch (err) {
        setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar las facturas');
      }
    })();
  }, [refrescar]);

  async function emitir() {
    if (!empresaId) return;
    setEmitiendo(true);
    setError('');
    try {
      const f = await api.post<FacturaDTO>('/api/operador/facturas', { empresaId, desde, hasta });
      await refrescar();
      toast.mostrar(`Factura ${f.numero} emitida`);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo emitir la factura');
    } finally {
      setEmitiendo(false);
    }
  }

  async function cobrar(id: string) {
    try {
      await api.post(`/api/operador/facturas/${id}/cobrada`);
      await refrescar();
      toast.mostrar('Factura marcada como cobrada');
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo actualizar');
    }
  }

  async function anular(id: string) {
    try {
      await api.post(`/api/operador/facturas/${id}/anular`);
      await refrescar();
      toast.mostrar('Factura anulada');
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo anular');
    }
  }

  async function verPdf(id: string) {
    try {
      await abrirPdf(`/api/operador/facturas/${id}/pdf`);
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : String(err));
    }
  }

  const kpis = useMemo(() => {
    const activas = (facturas ?? []).filter((f) => f.estado !== 'ANULADA');
    const emitido = activas.reduce((s, f) => s + f.totalCentavos, 0);
    const cobrado = activas.filter((f) => f.estado === 'COBRADA').reduce((s, f) => s + f.totalCentavos, 0);
    return { emitido, cobrado, pendiente: emitido - cobrado };
  }, [facturas]);

  return (
    <section>
      <h2>Facturas a empresas</h2>
      <p className="muted">
        Emití el comprobante de las visitas de control de una empresa en el período. La unidad es la
        visita atendida (las rectificaciones no se cuentan dos veces).
      </p>

      <div className="filtro-periodo">
        <div className="campo">
          <label htmlFor="fac-empresa">Empresa</label>
          <select id="fac-empresa" value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>{e.nombre}</option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="fac-desde">Desde</label>
          <input id="fac-desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="fac-hasta">Hasta</label>
          <input id="fac-hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <button className="primario" style={{ width: 'auto' }} onClick={() => void emitir()} disabled={emitiendo || !empresaId}>
          {emitiendo ? 'Emitiendo…' : 'Emitir factura'}
        </button>
      </div>

      {error && <div className="error-box" role="status">{error}</div>}

      <div className="grid-tarjetas" style={{ marginTop: 18 }}>
        <div className="kpi">
          <span className="kpi-rotulo">Emitido</span>
          <span className="kpi-monto">{formatearMonedaArs(kpis.emitido)}</span>
        </div>
        <div className="kpi kpi-ganancia">
          <span className="kpi-rotulo">Cobrado</span>
          <span className="kpi-monto">{formatearMonedaArs(kpis.cobrado)}</span>
        </div>
        <div className="kpi">
          <span className="kpi-rotulo">Pendiente de cobro</span>
          <span className="kpi-monto">{formatearMonedaArs(kpis.pendiente)}</span>
        </div>
      </div>

      <h3 style={{ marginTop: 24 }}>Comprobantes emitidos</h3>
      {facturas === null ? (
        <div className="cargando">Cargando…</div>
      ) : facturas.length === 0 ? (
        <p className="muted">Todavía no emitiste facturas. Elegí una empresa y un período y emití la primera.</p>
      ) : (
        <table className="tabla">
          <thead>
            <tr>
              <th scope="col">Número</th>
              <th scope="col">Empresa</th>
              <th scope="col">Período</th>
              <th scope="col">Visitas</th>
              <th scope="col">Total</th>
              <th scope="col">Estado</th>
              <th scope="col" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {facturas.map((f) => (
              <tr key={f.id}>
                <td><span className="mini" style={{ fontFamily: 'monospace' }}>{f.numero}</span></td>
                <td>{f.contraparteNombre}</td>
                <td className="mini">{f.periodo.desde} → {f.periodo.hasta}</td>
                <td>{f.visitas}</td>
                <td>{formatearMonedaArs(f.totalCentavos)}</td>
                <td><span className={`insignia ${COLOR_ESTADO_FACTURA[f.estado]}`}>{ETIQUETA_ESTADO_FACTURA[f.estado]}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {f.estado === 'EMITIDA' && (
                      <button className="secundario" style={{ width: 'auto' }} onClick={() => void cobrar(f.id)}>Marcar cobrada</button>
                    )}
                    <button className="secundario" style={{ width: 'auto' }} onClick={() => void verPdf(f.id)}>PDF</button>
                    {f.estado === 'EMITIDA' && (
                      <button className="fantasma" style={{ width: 'auto' }} onClick={() => void anular(f.id)}>Anular</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
